/**
 * Every read and write this application performs, named and typed.
 *
 * Row shapes are transcribed from the migrations in `supabase/migrations`, not
 * from `packages/contracts/database.types.ts`, because those types are generated
 * from a live catalog and this workspace does not run the database. Where a
 * column is `bigint`, `numericValue` accepts the string form PostgREST may
 * return, because a file size that renders as `NaN` is worse than one that
 * renders as a string.
 *
 * Nothing here invents a record, and nothing here reads a surface that does not
 * exist. Where a screen needs something the deployment cannot supply, the query
 * that would supply it is absent and the screen says so.
 */

import { sha256Hex } from '../lib/sha256.js';
import { type ApiEnvelope, type Client, fail, ok, type Result } from './client.js';

export interface SkillRow {
  readonly id: string;
  readonly org_id: string;
  readonly slug: string;
  readonly title: string;
  readonly description: string | null;
  readonly lifecycle: string;
  readonly owner_id: string;
  readonly current_release_id: string | null;
  readonly replaced_by_skill_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly version: number;
}

export interface ReleaseRow {
  readonly id: string;
  readonly org_id: string;
  readonly skill_id: string;
  readonly revision: string;
  readonly git_commit: string;
  readonly manifest_digest: string;
  readonly changelog: string | null;
  readonly published_by: string;
  readonly published_at: string;
}

export interface ReleaseFileRow {
  readonly id: string;
  readonly release_id: string;
  readonly path: string;
  readonly content_hash: string;
  readonly bytes: number | string;
}

export interface DraftRow {
  readonly id: string;
  readonly org_id: string;
  readonly skill_id: string;
  readonly base_release_id: string | null;
  readonly author_id: string;
  readonly version: number;
  readonly state: string;
  readonly change_reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface DraftFileRow {
  readonly id: string;
  readonly draft_id: string;
  readonly path: string;
  readonly content_hash: string;
  readonly storage_key: string;
  readonly bytes: number | string;
}

export interface ReviewRow {
  readonly id: string;
  readonly org_id: string;
  readonly draft_id: string;
  readonly reviewer_id: string;
  readonly decision: string;
  readonly approved_content_hash: string | null;
  readonly checklist: Record<string, unknown>;
  readonly self_review: boolean;
  readonly decided_at: string;
}

export interface ToolkitRow {
  readonly id: string;
  readonly org_id: string;
  readonly slug: string;
  readonly title: string;
  readonly description: string | null;
  readonly owner_id: string;
  readonly version: number;
}

export interface ToolkitItemRow {
  readonly id: string;
  readonly toolkit_id: string;
  readonly skill_id: string;
  readonly sort_order: number;
}

export interface ProposalRow {
  readonly id: string;
  readonly org_id: string;
  readonly skill_id: string;
  readonly base_release_id: string | null;
  readonly reporter_id: string;
  readonly category: string;
  readonly summary: string;
  readonly status: string;
  readonly linked_draft_id: string | null;
  readonly linked_release_id: string | null;
  readonly resolved_by: string | null;
  readonly resolved_at: string | null;
  readonly resolution_note: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface MembershipRow {
  readonly id: string;
  readonly org_id: string;
  readonly user_id: string;
  readonly role: string;
  readonly state: string;
}

/**
 * A resolved person, as `GET /v1/people` returns one.
 *
 * Transcribed from `apps/api/src/routes/people.ts` rather than from
 * `packages/contracts/database.types.ts`, like every other row in this file:
 * that file is generated from a live catalog and this workspace does not run
 * the database, and `auth.users` is not a table this application owns.
 */
export interface PersonRow {
  readonly user_id: string;
  readonly email: string;
}

/**
 * `/v1/people` as it arrives over the wire: nested under `data.people` and
 * camelCase, unlike the snake_case rows every other read route returns.
 * `listPeople` adapts it to `PersonRow` so nothing downstream has to know.
 */
interface RawPersonRow {
  readonly userId: string;
  readonly email: string;
}

export interface InviteRow {
  readonly id: string;
  readonly org_id: string;
  readonly email: string;
  readonly role: string;
  readonly expires_at: string;
  readonly accepted_at: string | null;
  readonly created_at: string;
}

export interface ClientGrantRow {
  readonly id: string;
  readonly org_id: string;
  readonly actor_id: string;
  readonly client_id: string;
  readonly capabilities: readonly string[];
  readonly revoked_at: string | null;
  readonly created_at: string;
}

export interface AuditEventRow {
  readonly id: string;
  readonly org_id: string;
  readonly actor_id: string | null;
  readonly actor_kind: string;
  readonly target_kind: string;
  readonly target_id: string | null;
  readonly action: string;
  readonly result: string;
  readonly metadata: Record<string, unknown>;
  readonly occurred_at: string;
}

/**
 * One observed delivery, as `GET /v1/retrieval-events` projects it.
 *
 * Deliberately a subset of the table: the route returns the delivery's identity
 * and provenance and nothing else. There is no prompt, transcript or session
 * column on `app.retrieval_events` to project — V1 §5:213 scopes the table to
 * "observed delivery events without conversation bodies" — and `updated_at`
 * and `version` are omitted because the table is append-only, so there is no
 * second version of a delivery to describe.
 *
 * `client_id` is nullable and MEASURED to be nullable most of the time: a
 * bundle download by a person rather than a client records `actor_id` with no
 * `client_id`. A caller that assumed it populated would read those rows as
 * absent rather than as differently-shaped, which is an empty result wearing
 * the costume of an answer.
 */
export interface RetrievalEventRow {
  readonly id: string;
  readonly org_id: string;
  readonly skill_id: string;
  readonly release_id: string;
  readonly actor_id: string | null;
  readonly client_id: string | null;
  readonly retrieved_at: string;
}

export interface OrganizationRow {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly allow_self_review: boolean;
  readonly version: number;
}

interface SkillGrantRow {
  readonly id: string;
  readonly skill_id: string;
  readonly principal_kind: string;
  readonly principal_id: string;
  readonly permission: string;
  readonly revoked_at: string | null;
}

export interface GroupRow {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}

/** A skill as the library screen needs it: the row plus its current revision. */
export interface LibrarySkill {
  readonly skill: SkillRow;
  readonly release: ReleaseRow | null;
}

export function numericValue(value: number | string | null | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/**
 * Read one page from a contract listing route and unwrap the envelope.
 *
 * `paginate()` answers with `{request_id, data, page:{nextCursor}}`, and
 * `nextCursor` is the ONLY end-of-set signal (packages/contracts/src/pagination.ts:48).
 * A short page is not an end of set: a filter can legitimately return fewer rows
 * than were asked for while more remain. So "are there more" is always answered
 * by reading the cursor, never by comparing the row count to the limit.
 *
 * The cursor is returned rather than a boolean because a caller that wants to
 * go on to page two needs the cursor itself.
 */
async function apiList<T>(
  client: Client,
  path: string,
  query: Readonly<Record<string, string>> = {},
): Promise<Result<{ readonly rows: readonly T[]; readonly nextCursor: string | null }>> {
  const response = await client.api<ApiEnvelope<readonly T[]>>(`${path}?${qs(query)}`);
  if (!response.ok) return response;
  return ok({ rows: response.value.data, nextCursor: response.value.page?.nextCursor ?? null });
}

/**
 * Follow `nextCursor` until `wanted` rows are gathered or the set ends.
 *
 * `PAGE_SIZE_MAX` is 100 and an out-of-range `limit` is a 422 rather than a
 * silent clamp, so a screen that wants 500 audit rows cannot ask for 500 in one
 * call. This pages for it, and stops the moment the cursor is null.
 *
 * The loop is bounded by `wanted` rather than by a page count: `rows.length`
 * grows by at least the page size each turn, so it terminates, and a route that
 * keeps handing back a cursor with an empty page cannot spin here.
 */
async function collect<T>(
  client: Client,
  path: string,
  wanted: number,
  query: Readonly<Record<string, string>> = {},
): Promise<Result<readonly T[]>> {
  const rows: T[] = [];
  let cursor = '';
  while (rows.length < wanted) {
    const page = await apiList<T>(client, path, {
      ...query,
      limit: String(Math.min(wanted - rows.length, 100)),
      ...(cursor === '' ? {} : { cursor }),
    });
    if (!page.ok) return page;
    rows.push(...page.value.rows);
    const next = page.value.nextCursor;
    if (next === null || page.value.rows.length === 0) break;
    cursor = next;
  }
  return ok(rows);
}

/** Query parameters, skipping absent ones so no route sees `?owner_id=undefined`. */
function qs(query: Readonly<Record<string, string>>): string {
  return new URLSearchParams(
    Object.entries(query).filter((entry): entry is [string, string] => entry[1] !== ''),
  ).toString();
}

/**
 * Several records by id, fetched one route call each.
 *
 * `/v1/releases/:releaseId` and `/v1/skills/:skillId` are single-record routes;
 * there is no bulk `?ids=` form in the contract. That makes this an N+1, which
 * is a property of the agreed contract rather than an oversight, so it is stated
 * here instead of hidden. A record the policies hide 404s, and a 404 here is
 * dropped rather than propagated: the caller's question was "which of these do I
 * have", and a row it may not see is not an error, it is an absent row.
 */
async function loadById<T>(
  client: Client,
  path: (id: string) => string,
  ids: readonly string[],
): Promise<Result<readonly T[]>> {
  if (ids.length === 0) return ok<readonly T[]>([]);
  const responses = await Promise.all(ids.map((id) => client.api<ApiEnvelope<T>>(path(id))));
  const rows: T[] = [];
  for (const response of responses) {
    if (!response.ok) {
      if (response.failure.kind === 'not-found') continue;
      return response;
    }
    rows.push(response.value.data);
  }
  return ok(rows);
}

/** Sort a listing the way the screen reads, because the API's keyset does not. */
/**
 * Sort a listing the way the screen reads it, because the API's keyset is fixed
 * at `created_at desc, id desc` and says nothing about what this screen wants.
 *
 * A descending sort is expressed by reversing the comparator rather than by
 * `.reverse()` afterwards, because a reversed ascending sort is not the same
 * thing on a list with equal keys: reversing would order equals backwards while
 * leaving them grouped, and the order within a tie is what a reader sees.
 */
function byField<T>(
  rows: readonly T[],
  field: (row: T) => string,
  direction: 'asc' | 'desc' = 'asc',
): readonly T[] {
  const sign = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => sign * field(a).localeCompare(field(b)));
}

/* -------------------------------------------------------------------------
 * Organization
 * ---------------------------------------------------------------------- */

export async function getOrganization(client: Client): Promise<Result<OrganizationRow>> {
  // Through the API, not the Data API. `supabase/config.toml` lists
  // `schemas = ["public", "graphql_public"]`, so `app` is unreachable over
  // PostgREST — and adding it would open a path around the per-request role
  // assertion the API depends on. The route takes no organization id: the one it
  // returns is the one in the verified token, so no call can express "some other
  // organization".
  const response = await client.api<ApiEnvelope<OrganizationRow>>('/v1/organizations/current');
  if (!response.ok) return response;
  const row = response.value.data;
  return row === undefined || row === null
    ? fail({ kind: 'not-found', reason: 'not_found' })
    : ok(row);
}

/* -------------------------------------------------------------------------
 * Library
 * ---------------------------------------------------------------------- */

/**
 * The library listing.
 *
 * V1 section 9 requires the default to be usable published skills, and the
 * filter is applied here rather than in the view: a skill with no current
 * release cannot be used, so offering it on the default page would offer
 * something that fails on click. "My drafts" is a different table entirely,
 * because a draft belongs to a person rather than to the catalog.
 */
export async function listSkills(
  client: Client,
  orgId: string,
  filters: {
    readonly q: string;
    readonly owner: string;
    readonly status: string;
    readonly limit: number;
  },
): Promise<Result<{ readonly rows: readonly LibrarySkill[]; readonly truncated: boolean }>> {
  // Every listing route is bound to the organization in the verified token, so
  // this id cannot narrow the result and is not sent. It stays in the signature
  // because the screens already thread it through, and because dropping it would
  // make the call look organization-agnostic when it is not.
  void orgId;

  // Search is server-side now, and TITLE-ONLY, which is a constraint rather than
  // a preference. `paginate()` ANDs its predicates, so a second TextSearchFilter
  // would render `title ilike … and slug ilike …` — a skill whose title AND slug
  // both contain the term, which is narrower than what a person typing in a
  // search box means and would read as search "sort of not working" rather than
  // as a bug. Multi-column search needs an `OrFilter` that does not exist in
  // contracts yet, so `title` alone is the honest scope. `description` is not
  // searched either.
  //
  // The term is length-bounded and LIKE-escaped by contracts, so a person typing
  // `%` searches for a percent sign instead of matching every row.
  const page = await apiList<SkillRow>(client, '/v1/skills', {
    q: filters.q.trim(),
    owner_id: filters.owner === 'all' ? '' : filters.owner,
    lifecycle: filters.status === 'all' ? '' : filters.status,
    limit: String(Math.min(filters.limit, 100)),
  });
  if (!page.ok) return page;

  // The keyset is `created_at desc, id desc` for every listing, so the screen's
  // "most recently updated first" is applied here rather than assumed.
  const skills = byField(page.value.rows, (row) => row.updated_at, 'desc');

  const releases = await loadReleases(
    client,
    skills
      .map((skill) => skill.current_release_id)
      .filter((id): id is string => typeof id === 'string'),
  );
  if (!releases.ok) return releases;

  const byId = new Map(releases.value.map((release) => [release.id, release]));
  return ok({
    rows: skills.map((skill) => ({
      skill,
      release: byId.get(skill.current_release_id ?? '') ?? null,
    })),
    // The cursor, not the length: a filtered page shorter than the limit is not
    // an end of set.
    truncated: page.value.nextCursor !== null,
  });
}

export async function loadReleases(
  client: Client,
  ids: readonly string[],
): Promise<Result<readonly ReleaseRow[]>> {
  return loadById<ReleaseRow>(client, (id) => `/v1/releases/${encodeURIComponent(id)}`, ids);
}

export async function listOwnDrafts(
  client: Client,
  orgId: string,
  userId: string,
): Promise<Result<readonly DraftRow[]>> {
  void orgId;
  const page = await apiList<DraftRow>(client, '/v1/drafts', { author_id: userId });
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.updated_at, 'desc'));
}

/**
 * Drafts awaiting a decision, which is what the review count in the rail means.
 *
 * Two listings, not one: `paginate()` filters by column equality, so
 * `state=in.(in_review,approved)` has no equivalent here. Two equalities, two
 * reads, concatenated — rather than one read of one state, which would silently
 * under-count the rail by whatever the second state held.
 */
export async function listReviewableDrafts(
  client: Client,
  orgId: string,
): Promise<Result<readonly DraftRow[]>> {
  void orgId;
  // ONE request, not two. `state = any($1)` takes both states in a single
  // predicate, so the rail cannot silently under-count itself. The previous shape
  // read one state and dropped the other whenever a page ran short, which is the
  // exact failure a count in a rail must not have.
  const page = await apiList<DraftRow>(client, '/v1/drafts', {
    state: 'in_review,approved',
  });
  if (!page.ok) return page;
  // The server keyset is `created_at desc`; this list reads as most-recently-updated.
  return ok(byField(page.value.rows, (row) => row.updated_at, 'desc'));
}

export interface SkillDetail {
  readonly skill: SkillRow;
  /** From the API contract read, which is what reports lifecycle warnings. */
  readonly warnings: readonly string[];
  readonly release: ReleaseRow | null;
  readonly releaseFiles: readonly ReleaseFileRow[];
  readonly drafts: readonly DraftRow[];
  readonly proposals: readonly ProposalRow[];
  readonly grants: readonly SkillGrantRow[];
}

/**
 * The skill as `GET /v1/skills/:skillId` projects it.
 *
 * A superset of `SkillRow` minus `created_at` ordering concerns: the route adds
 * `published` and `warnings`, and `warnings` is what the detail screen renders
 * for a deprecated skill. `owner_id` and `replaced_by_skill_id` are here because
 * `views/library.ts` and `views/skill.ts` read them off this same record.
 */
interface ApiSkill extends SkillRow {
  readonly warnings?: readonly string[];
}

export async function getSkillDetail(
  client: Client,
  orgId: string,
  skillId: string,
): Promise<Result<SkillDetail>> {
  void orgId;
  // One read for the record, not two. The old shape asked the API for the
  // lifecycle contract and then went back to PostgREST for the row itself; the
  // single-record route carries both, so the second call is gone rather than
  // merged. A skill the policies hide is a 404 from this one route, which is the
  // same answer a fabricated id gets — there is no existence check anywhere.
  const response = await client.api<ApiEnvelope<ApiSkill>>(
    `/v1/skills/${encodeURIComponent(skillId)}`,
  );
  if (!response.ok) return response;
  const skill = response.value.data;

  const [releases, drafts, proposals, grants] = await Promise.all([
    loadReleases(client, skill.current_release_id === null ? [] : [skill.current_release_id]),
    listSkillDrafts(client, skillId),
    apiList<ProposalRow>(client, '/v1/proposals', { skill_id: skillId }),
    apiList<SkillGrantRow>(client, '/v1/skill-grants', { skill_id: skillId, active: 'true' }),
  ]);

  if (!releases.ok) return releases;
  if (!drafts.ok) return drafts;
  if (!proposals.ok) return proposals;
  if (!grants.ok) return grants;

  const release = releases.value[0] ?? null;
  const files =
    release === null
      ? ok<readonly ReleaseFileRow[]>([])
      : await listReleaseFiles(client, release.id);
  if (!files.ok) return files;

  return ok({
    releaseFiles: files.value,
    skill,
    warnings: skill.warnings ?? [],
    release,
    drafts: drafts.value,
    proposals: byField(proposals.value.rows, (row) => row.created_at, 'desc'),
    // `active=true` becomes `revoked_at is null` in the query, before the limit.
    // Filtering here after the page was taken would spend a page of grants and
    // discard the revoked ones, so a skill with a long grant history returned a
    // short list and a cursor that looked like the end of the data.
    //
    // Named `active`, not `pending`, and the distinction is not cosmetic: a grant
    // is not "pending", it is active or revoked. `/v1/invites?pending=true` really
    // does mean an unaccepted invite. One word meaning two different domain states
    // across two routes is how a caller filters on the wrong one and gets a
    // complete, plausible, wrong list.
    grants: grants.value.rows,
  });
}

/** The drafts of one skill, which the editor and the detail screen both list. */
export async function listSkillDrafts(
  client: Client,
  skillId: string,
): Promise<Result<readonly DraftRow[]>> {
  const page = await apiList<DraftRow>(client, '/v1/drafts', { skill_id: skillId });
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.updated_at, 'desc'));
}

export async function listReleaseFiles(
  client: Client,
  releaseId: string,
): Promise<Result<readonly ReleaseFileRow[]>> {
  const page = await apiList<ReleaseFileRow>(
    client,
    `/v1/release-files/${encodeURIComponent(releaseId)}`,
  );
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.path));
}

/* -------------------------------------------------------------------------
 * Drafts
 * ---------------------------------------------------------------------- */
export async function getDraft(client: Client, draftId: string): Promise<Result<DraftRow>> {
  // A draft the policies hide and a draft that does not exist are the same 404,
  // produced by the route from one filtered primary-key lookup. There is no
  // existence check on this side to tell them apart, by design.
  const response = await client.api<ApiEnvelope<DraftRow>>(
    `/v1/drafts/${encodeURIComponent(draftId)}`,
  );
  if (!response.ok) return response;
  return ok(response.value.data);
}

export async function listDraftFiles(
  client: Client,
  draftId: string,
): Promise<Result<readonly DraftFileRow[]>> {
  const page = await apiList<DraftFileRow>(
    client,
    `/v1/draft-files/${encodeURIComponent(draftId)}`,
  );
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.path));
}

export async function listReviews(
  client: Client,
  draftId: string,
): Promise<Result<readonly ReviewRow[]>> {
  const page = await apiList<ReviewRow>(client, '/v1/reviews', { draft_id: draftId });
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.decided_at, 'desc'));
}

/**
 * Save a draft through the API's optimistic-lock route.
 *
 * The route refuses a stale `If-Match` with 409 and reports the current version,
 * which is what the editor turns into a conflict prompt rather than an
 * overwrite.
 *
 * `files` carries CONTENT, and only content. `content_hash`, `bytes` and
 * `storage_key` are derived server-side from the bytes that arrive, so a wire
 * that carried a digest would let the browser assert a value the server then
 * trusts and records — the client would be vouching for a hash it is not
 * trusted to compute over bytes it did not write. The projection below is the
 * last point before the wire and it is deliberately narrow: whatever a caller
 * hands in, those three keys cannot leave the browser.
 */
export async function saveDraft(
  client: Client,
  skillId: string,
  draftId: string,
  expectedVersion: number,
  changeReason: string,
  idempotencyKey: string,
  files: readonly { readonly path: string; readonly content: string }[],
): Promise<Result<{ readonly version: number; readonly state: string }>> {
  const response = await client.api<
    ApiEnvelope<{ readonly draft_id: string; readonly version: number; readonly state: string }>
  >(`/v1/skills/${skillId}/drafts/${draftId}`, {
    method: 'PUT',
    headers: { 'if-match': `"${expectedVersion}"`, 'idempotency-key': idempotencyKey },
    body: {
      changeReason,
      files: files.map((file) => ({ path: file.path, content: file.content })),
    },
  });
  if (!response.ok) return response;
  return ok({ version: response.value.data.version, state: response.value.data.state });
}

/**
 * The stored bytes of one file in a draft.
 *
 * A DIFFERENT ROUTE from the one that writes it, and that is the whole reason
 * this exists: the save answers with a version, this answers with the document,
 * so a save that reported success without persisting anything is visible here
 * rather than confirmed by its own response. The editor's post-condition
 * compares the two.
 *
 * THE ROUTE ANSWERS AN ENVELOPE ROW, not bytes:
 * `{ draft_id, path, bytes, content_hash, content }` inside the standard
 * wrapper, of which only `content` is the document. `downloadBundle` reads a raw
 * body because the bundle route serves bytes; this one does not, and reading it
 * as text put a JSON document into the editor's textarea — which a browser
 * found and which no unit test against a string-returning stub could have.
 *
 * WHAT THE CALLER MAY RELY ON, and it is deliberately narrower than the route's
 * own contract. `content_hash` is carried here, and the route is specified to
 * verify what it serves against the row's recorded digest and refuse on a
 * mismatch — **that is the API's claim and this client has never exercised it
 * against a server.** What the editor actually checks is the weaker thing it
 * can check for itself: that what came back equals what it sent.
 */
export async function readDraftFile(
  client: Client,
  draftId: string,
  path?: string,
): Promise<Result<string>> {
  const query = path === undefined ? '' : `?${qs({ path })}`;
  // The ENVELOPE, not a raw body. The route answers `{ draft_id, path, bytes,
  // content_hash, content }` inside the standard wrapper, so reading it as text
  // put the whole JSON document into the editor's textarea — which is what a
  // browser found and what no unit test could have: `downloadBundle` is raw
  // because the bundle route serves bytes, and this route does not.
  // `client.api<T>` returns `Result<T>`, and every sibling here passes
  // `ApiEnvelope<Row>` and reads `.data` — the route's JSON body, not the wire
  // envelope. Passing the bare row made `response.value.data` a type error, which
  // is the only reason this was ever caught: the test that found the real bug
  // (a JSON document landing in a textarea) could not have run.
  const response = await client.api<
    ApiEnvelope<{
      readonly draft_id: string;
      readonly path: string;
      readonly bytes: number;
      readonly content_hash: string;
      readonly content: string;
    }>
  >(`/v1/drafts/${encodeURIComponent(draftId)}/files${query}`);
  if (!response.ok) return response;
  return ok(response.value.data.content);
}

/**
 * Move a draft into review.
 *
 * `POST /v1/drafts/:draftId/submit`, which wraps `app.submit_draft_for_review`.
 *
 * THE STATE IS NOT A PARAMETER, and `state` is here only so callers do not
 * have to change: there is no `app.set_draft_state`, and a general "set state"
 * endpoint would have to choose WHICH governed RPC to call — a second state
 * machine, written in TypeScript, free to disagree with the one in SQL. The
 * route names the transition instead, so anything other than `'in_review'` is
 * refused HERE, before a request is made, rather than being sent as a value
 * the server would drop. A caller that sent `'approved'` and got a 200 would
 * render a draft as approved that no row says is.
 *
 * The row the route sends back is what is returned, not the state that was
 * asked for: the server's answer is the only statement about the draft.
 *
 * `changeReason` is part of the signature the callers already hold and is
 * deliberately NOT sent. The route refuses every field a body could carry
 * (`assertNoSelfNamedFields`), so there is no request shape in which a reason
 * recorded here would reach the row; a submit that silently discarded one
 * would leave the editor showing text it never stored.
 *
 * The idempotency key is minted HERE rather than handed in, because this
 * function's signature is fixed by its callers and adding a parameter would
 * change it. `crypto.randomUUID()` per attempt is the same rule `actions.ts`
 * mints by: a replay is safe when one key serves one attempt, so a retry after
 * a dropped connection gets a new key and is refused by the state machine
 * rather than silently creating a second review.
 */
export async function setDraftState(
  client: Client,
  draftId: string,
  state: string,
  changeReason: string | null,
): Promise<Result<DraftRow>> {
  if (state !== 'in_review') {
    return fail({
      kind: 'invalid',
      reason: 'unsupported_draft_transition',
      detail:
        `This build has one governed transition for a draft, and "${state}" is not it. ` +
        'Submitting is the only one a browser can perform; approval and requesting changes go ' +
        'through the review decision, not through a state this client chooses.',
    });
  }
  void changeReason;
  const response = await client.api<ApiEnvelope<DraftRow>>(
    `/v1/drafts/${encodeURIComponent(draftId)}/submit`,
    { method: 'POST', headers: { 'idempotency-key': crypto.randomUUID() } },
  );
  if (!response.ok) return response;
  return ok(response.value.data);
}

/**
 * Create the draft a revision is written in.
 *
 * The organization is NOT a parameter, for the reason `createSkill` does not
 * take one: `app.create_draft` takes both, but the route fills the
 * organization from the verified token and the author from
 * `authz._actor_id()` inside the RPC, and answers 422 to a body naming
 * `org_id` or `author_id`. A browser that names its own tenant or its own
 * author is asserting who it is, and refusing that assertion is what makes
 * it impossible rather than merely ignored.
 *
 * The skill is in the path, so there is nothing left for a body to carry and
 * none is sent: the route refuses every field a body could hold, and an empty
 * one is the only shape it accepts. This is phase one of a two-phase write —
 * the storage key `<org_id>/<skill_id>/<draft_id>/<path>` cannot be built
 * until the id comes back — so the returned row is the whole product of this
 * call, and `draft_id` is the field the next step needs.
 */
export async function createDraft(
  client: Client,
  skillId: string,
  idempotencyKey: string,
): Promise<Result<DraftRow>> {
  const response = await client.api<ApiEnvelope<DraftRow>>(
    `/v1/skills/${encodeURIComponent(skillId)}/drafts`,
    { method: 'POST', headers: { 'idempotency-key': idempotencyKey } },
  );
  if (!response.ok) return response;
  return ok(response.value.data);
}

/**
 * Record a review decision.
 *
 * `approved_content_hash` is not optional in the schema, and it is the whole
 * point of PUB-01: an approval binds to the exact bytes reviewed, so a later
 * edit invalidates it by changing the hash rather than by editing the approval.
 * `contentBinding` in `review-model.ts` computes what that hash is, and
 * `app.approve_draft` recomputes it server-side and refuses the approval if the
 * two disagree — so a screen that computed it wrongly is refused rather than
 * producing an approval bound to nothing.
 *
 * IT IS TWO ROUTES, NOT ONE, because `apps/api` names the decision in the path
 * (`POST /v1/drafts/:draftId/approve`, `.../request-changes`) rather than taking
 * a `decision` field. That is the same rule `setDraftState` follows for
 * submission: a general "set state" endpoint would be a second state machine
 * written in TypeScript that could disagree with the one in SQL.
 *
 * `orgId` is no longer a parameter. It was, in the shape that shipped as a
 * stub, because nothing was written; the organization comes from the verified
 * token and a body naming it is refused at the route.
 */
export async function recordReview(
  client: Client,
  input: {
    readonly draftId: string;
    readonly decision: 'approved' | 'changes_requested';
    readonly approvedContentHash: string | null;
    readonly checklist: Record<string, unknown>;
    /**
     * Required only to send the draft back, and refused by the approve route.
     * Optional here rather than a second overload, because the decision is
     * already discriminated: sending `reason` on the approval branch would put
     * a field in the body that the route answers 422 for.
     */
    readonly reason?: string;
  },
  idempotencyKey: string,
): Promise<Result<ReviewRow>> {
  const path = `/v1/drafts/${encodeURIComponent(input.draftId)}`;
  const response =
    input.decision === 'approved'
      ? await client.api<ApiEnvelope<ReviewRow>>(`${path}/approve`, {
          method: 'POST',
          headers: { 'idempotency-key': idempotencyKey },
          body: {
            expected_content_hash: input.approvedContentHash ?? '',
            checklist: input.checklist,
          },
        })
      : await client.api<ApiEnvelope<ReviewRow>>(`${path}/request-changes`, {
          method: 'POST',
          headers: { 'idempotency-key': idempotencyKey },
          body: { reason: input.reason },
        });
  if (!response.ok) return response;
  return ok(response.value.data);
}

/* -------------------------------------------------------------------------
 * Publication
 * ---------------------------------------------------------------------- */

/**
 * The job `POST /v1/drafts/:draftId/publish` returns (202).
 *
 * A `202`, not a `201`, because nothing is published yet — the worker does that
 * afterwards, in another process.
 *
 * NOT THE HANDLE THE SCREEN POLLS, which is what this comment used to claim
 * and what the next sentence then contradicted. The screen polls
 * `getPublication`, which is draft-scoped, and never uses this row's `id`.
 * `@OidcLoginFlow` flagged the claim as asserting reachability nothing backs;
 * they were right, and the reason it is now certainly false rather than merely
 * unproven is the live catalog: `app.publish_job_status` resolves by
 * `p_draft_id`, so a poll keyed on this job id would be reading an endpoint
 * that does not exist.
 *
 * So this row is only ever the statement that the request was accepted, and
 * everything about the publication that is not yet true — whether the commit
 * landed, which release is current, why a worker gave up — is answered by
 * `getPublication`.
 */
export interface PublishJobRow {
  readonly id: string;
  readonly org_id: string;
  readonly skill_id: string;
  readonly draft_id: string;
  readonly state: string;
  readonly attempts: number;
  readonly expected_current_release_id: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly completed_at: string | null;
  readonly status_url: string;
}

/**
 * One row of `GET /v1/drafts/:draftId/publication` — the latest publication
 * job for a draft.
 *
 * DRAFT-SCOPED, and the draft-scoped version is the one that works. Publication
 * is asynchronous and outlives the tab that asked for it, so a person who
 * reloads arrives with a draft and no job id — and `last_error`, the one field
 * that says why a publication failed, lives on the job row. A job-scoped read
 * would answer "still publishing" forever for a publication that failed.
 */
export interface PublishJobStatusRow extends PublishJobRow {
  /** The worker's own `${code}: ${message}`, or null while it has not failed. */
  readonly last_error: string | null;
  readonly draft_state: string;
  /**
   * The SKILL's current release. Named for what it is: a later publication may
   * have moved it, so this is not a claim that it is this job's release.
 */
  readonly current_release_id: string | null;
  readonly current_revision: string | null;
}

/**
 * Ask for a publication.
 *
 * The idempotency key is minted HERE, once per attempt, and that is deliberate:
 * `publish_jobs.idempotency_key` is UNIQUE per organization and
 * `app.publish_draft` looks it up AFTER permission and BEFORE the draft-state
 * check (`0033:1312-1333`), so a retry with the SAME key returns the same job
 * and a retry with a NEW key is refused by the state machine — which is the
 * correct answer, because a second publication is a permanent release
 * revision. One key per attempt is therefore what PUB-03 asks for, and a
 * caller that presses the button twice gets the second press refused rather
 * than a duplicate release.
 */
export async function publishDraft(
  client: Client,
  draftId: string,
  idempotencyKey: string,
): Promise<Result<PublishJobRow>> {
  const response = await client.api<ApiEnvelope<PublishJobRow>>(
    `/v1/drafts/${encodeURIComponent(draftId)}/publish`,
    { method: 'POST', headers: { 'idempotency-key': idempotencyKey } },
  );
  if (!response.ok) return response;
  return ok(response.value.data);
}

/**
 * The draft states in which NO publication job can exist, and therefore a 404
 * from the publication route means "never published" rather than "hidden".
 *
 * THIS IS THE DISCRIMINATOR, AND IT IS A CLOSED SET FOR A REASON. `app.publish_draft`
 * inserts the job row and sets the draft to `publishing` in ONE transaction
 * (`0033:1413-1454`), so a job row cannot exist without that draft having been
 * in `publishing` first. Enumerating every writer of `app.drafts.state` in the
 * migrations — `0033:648` ('draft'), `0033:787` ('in_review'), `0033:1008`
 * ('approved'), `0033:1159` ('draft'), `0033:1452` ('publishing'), `0044`/`0062`
 * ('draft'), `packages/worker/src/sql.ts` ('published'), `0058:229`
 * ('publish_failed') — a draft in one of the three states below has provably
 * never been handed to a worker.
 *
 * WHY NOT "ANY 404 IS NO PUBLICATION". `publish_job_status` returns zero rows
 * both for a draft with no job and for a job whose `manage` permission was
 * revoked mid-publication, and the route turns zero rows into one 404 on
 * purpose (ACL-01, `apps/api/src/routes/publication.ts:281-287`). Folding
 * every 404 would render that second case as "not published" — a false
 * statement in the safe direction, which is how a false statement ships. So a
 * draft in `publishing`, `published` or `publish_failed` keeps its 404 as a
 * FAILURE, and the panel says its status is unknown rather than absent.
 */
const NEVER_PUBLISHED_STATES: Record<string, true> = {
  draft: true,
  in_review: true,
  approved: true,
};

/**
 * Read one publication's progress. The poll, not a guess.
 *
 * `ok(null)` means THIS DRAFT HAS NEVER BEEN PUBLISHED, and it is returned
 * only for that case: a 404 against a draft whose own state proves no job can
 * exist. Every other failure — a 403, a 500, a dropped connection, and the
 * 404 that means a job exists but is hidden from this caller — is returned as
 * a failure, because `publicationFailure` and `publication: null` are
 * different claims and the panel renders them differently on purpose.
 *
 * `draftState` is a PARAMETER rather than something read here, because this
 * function must not issue a second request to decide a question the caller has
 * already answered: the review screen reads the draft before it reads the
 * publication, and re-reading it would be a second source of truth about which
 * draft this is.
 */
export async function getPublication(
  client: Client,
  draftId: string,
  draftState: string,
): Promise<Result<PublishJobStatusRow | null>> {
  const response = await client.api<ApiEnvelope<PublishJobStatusRow>>(
    `/v1/drafts/${encodeURIComponent(draftId)}/publication`,
  );
  if (!response.ok) {
    // The ONE case that is folded, and it is folded on the failure KIND rather
    // than the status number: `not-found` is what the route's shared 404 code
    // becomes, and nothing else on this route produces it.
    if (response.failure.kind === 'not-found' && NEVER_PUBLISHED_STATES[draftState] === true) {
      return ok(null);
    }
    return response;
  }
  return ok(response.value.data);
}

/* -------------------------------------------------------------------------
 * Skills and toolkits: creation
 * ---------------------------------------------------------------------- */

export async function createSkill(
  client: Client,
  input: {
    readonly slug: string;
    readonly title: string;
    readonly description?: string;
  },
  idempotencyKey: string,
): Promise<Result<SkillRow>> {
  // The organization and the owner are NOT parameters, and that is the point.
  // `app.create_skill` takes both, but the route fills them from the verified
  // token and answers 422 if a body carries `org_id` or `owner_id`. A browser that
  // names its own tenant or its own owner is asserting who it is, and refusing
  // that assertion is what makes it impossible rather than merely ignored.
  //
  // `0041_creation_rpcs.sql` also revoked the direct INSERT and dropped
  // `skills_create`, so this is now the only path that exists — and it is the
  // audited one, which is what the old PostgREST insert was not.
  const response = await client.api<ApiEnvelope<SkillRow>>('/v1/skills', {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
    body: {
      slug: input.slug,
      title: input.title,
      ...(input.description === undefined ? {} : { description: input.description }),
    },
  });
  if (!response.ok) return response;
  return ok(response.value.data);
}

export async function createToolkit(
  client: Client,
  input: {
    readonly slug: string;
    readonly title: string;
    readonly description?: string;
  },
  idempotencyKey: string,
): Promise<Result<ToolkitRow>> {
  // Same shape as `createSkill`, over `app.create_toolkit`.
  const response = await client.api<ApiEnvelope<ToolkitRow>>('/v1/toolkits', {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
    body: {
      slug: input.slug,
      title: input.title,
      ...(input.description === undefined ? {} : { description: input.description }),
    },
  });
  if (!response.ok) return response;
  return ok(response.value.data);
}

/* -------------------------------------------------------------------------
 * Toolkits
 * ---------------------------------------------------------------------- */

export async function listToolkits(
  client: Client,
  orgId: string,
): Promise<Result<readonly ToolkitRow[]>> {
  void orgId;
  const page = await apiList<ToolkitRow>(client, '/v1/toolkits');
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.title));
}

/**
 * The items of one toolkit, with the skills they name.
 *
 * The `can_see_toolkit_item` policy filters the items, and every item it returns
 * names a skill this caller may already read, so a member who may see the
 * toolkit but not a skill inside it receives fewer items. The count the screen
 * shows is therefore the count of items this caller may see, and the screen says
 * so rather than implying the toolkit is that small.
 */
export async function listToolkitContents(
  client: Client,
  toolkitId: string,
): Promise<Result<readonly { readonly item: ToolkitItemRow; readonly skill: SkillRow | null }[]>> {
  const items = await apiList<ToolkitItemRow>(
    client,
    `/v1/toolkit-items/${encodeURIComponent(toolkitId)}`,
  );
  if (!items.ok) return items;
  // Numeric, not lexical: `localeCompare` on "10" vs "2" puts 10 first, which
  // would silently reorder any toolkit with ten or more items.
  const ordered = [...items.value.rows].sort((a, b) => a.sort_order - b.sort_order);
  if (ordered.length === 0) {
    return ok<readonly { readonly item: ToolkitItemRow; readonly skill: SkillRow | null }[]>([]);
  }

  // No join: the route returns the four item columns only. The skill rows are a
  // second read, and a skill this caller may not see is dropped rather than
  // reported as a broken toolkit.
  const skills = await loadSkills(
    client,
    ordered.map((item) => item.skill_id),
  );
  if (!skills.ok) return skills;
  const byId = new Map(skills.value.map((skill) => [skill.id, skill]));
  return ok(ordered.map((item) => ({ item, skill: byId.get(item.skill_id) ?? null })));
}

/* -------------------------------------------------------------------------
 * Proposals
 * ---------------------------------------------------------------------- */

export async function listProposals(
  client: Client,
  orgId: string,
  status: string,
): Promise<Result<readonly (ProposalRow & { readonly skillTitle: string | null })[]>> {
  void orgId;
  const page = await apiList<ProposalRow>(client, '/v1/proposals', {
    status: status === 'all' ? '' : status,
  });
  if (!page.ok) return page;
  const ordered = byField(page.value.rows, (row) => row.created_at, 'desc');

  // `GET /v1/proposals` returns no skill title, so the titles beside each row are
  // a second read keyed by the skill ids already in hand.
  const skills = await loadSkills(
    client,
    ordered.map((row) => row.skill_id),
  );
  if (!skills.ok) return skills;
  const titles = new Map(skills.value.map((skill) => [skill.id, skill.title]));
  return ok(ordered.map((row) => ({ ...row, skillTitle: titles.get(row.skill_id) ?? null })));
}

export async function resolveProposal(
  client: Client,
  proposalId: string,
  input: {
    readonly decision: 'accepted' | 'dismissed';
    readonly note: string;
    readonly resolvedBy: string;
  },
): Promise<Result<ProposalRow>> {
  // The two decisions differ, and the difference is worth stating rather than
  // collapsing into one "unsupported".
  //
  // `dismissed`: `POST /v1/proposals/:proposalId/dismiss` exists, over
  // `app.dismiss_proposal` (0042_proposal_dismissal.sql), and takes the note
  // as `reason`. The browser is refused a blank or whitespace-only reason by
  // the server, the same way `apps/web/src/views/actions.ts` refuses one before
  // it leaves — two independent gates on the same requirement, because the
  // reason is what the audit row carries and a resolution nobody can later
  // explain is not a record. The actor is taken from the verified token by the
  // route, so nothing here asserts who is dismissing.
  //
  // `accepted`: STILL NOT WIRED, and deliberately. The accept route exists,
  // over `app.accept_proposal`, but requires a `draftId` in the body — a draft
  // carrying the corrected content, because a proposal row stores no files.
  // This caller has no draft and cannot invent one: creating one goes through
  // `app.save_draft`. Calling accept without a draft would be a 422 that reads
  // like a bug, and fabricating a draft to make the branch look migrated is
  // precisely the half-written write this codebase keeps cataloguing.
  if (input.decision === 'accepted') {
    return fail({
      kind: 'unavailable',
      what: 'accepting a proposal (needs a draftId carrying the corrected content)',
    });
  }

  const response = await client.api<ApiEnvelope<ProposalRow>>(
    `/v1/proposals/${encodeURIComponent(proposalId)}/dismiss`,
    {
      method: 'POST',
      headers: { 'idempotency-key': crypto.randomUUID() },
      body: { reason: input.note.trim() },
    },
  );
  if (!response.ok) return response;
  const row = response.value.data;
  return row === undefined || row === null
    ? fail({ kind: 'unavailable', what: 'dismissing a proposal' })
    : ok(row);
}

/* -------------------------------------------------------------------------
 * Connect: the caller's own OAuth client grants
 * ---------------------------------------------------------------------- */

export async function listClientGrants(
  client: Client,
  orgId: string,
  actorId: string,
): Promise<Result<readonly ClientGrantRow[]>> {
  void orgId;
  const page = await apiList<ClientGrantRow>(client, '/v1/client-grants', {
    actor_id: actorId,
  });
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.created_at, 'desc'));
}
/**
 * Revocation goes through the API, not through a direct write.
 *
 * V1 section 4 rule 4 withholds ACL management from OAuth clients, and
 * `app.revoke_client_grant` is the audited path. The actor is taken from the
 * verified token by the route, so nothing about who is revoking is asserted by
 * this caller.
 */
export async function revokeClientGrant(
  client: Client,
  clientId: string,
  idempotencyKey: string,
): Promise<Result<{ readonly clientId: string; readonly revoked: number }>> {
  // `app.revoke_client_grant` is declared with return type `integer`: the number
  // of grant rows it tombstoned (0030_constant_work_visibility_check.sql:947).
  // `callRpc` reads it with `select * from app.<fn>(...)`, which names the
  // function. It does NOT return a row with a `client_id`.
  //
  // This mattered because the count is the only proof the write did anything.
  // The previous shape read `data.client_id`, which is absent, and reported a
  // successful revocation of "undefined", including when the count was zero
  // and nothing had been revoked at all.
  const response = await client.api<ApiEnvelope<{ readonly revoke_client_grant: number }>>(
    `/v1/clients/${encodeURIComponent(clientId)}/revoke`,
    {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
    },
  );
  if (!response.ok) return response;
  return ok({ clientId, revoked: response.value.data.revoke_client_grant });
}

/* -------------------------------------------------------------------------
 * Administration
 * ---------------------------------------------------------------------- */

export async function listMemberships(
  client: Client,
  orgId: string,
): Promise<Result<readonly MembershipRow[]>> {
  void orgId;
  const page = await apiList<MembershipRow>(client, '/v1/memberships');
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.role));
}

/**
 * The people of the organization in the session's token, by `user_id`.
 *
 * One call per navigation, not one per person: the review screen names a
 * submitter, several reviewers and a publisher, the library names an owner per
 * row, and a per-id read would make the number of requests a function of how
 * much history the organization has. `orgId` is deliberately NOT sent — the API
 * scopes the read to the verified token's `org_id` (`routes/people.ts`), and a
 * scope a caller can pass is a scope a caller can choose.
 */
export async function listPeople(
  client: Client,
  orgId: string,
): Promise<Result<readonly PersonRow[]>> {
  void orgId;
  // `/v1/people` answers `{ data: { people: [...] } }` with camelCase keys and
  // no `page` sibling — it does not use the §6 listing envelope every other
  // read route uses (`/v1/invites` answers `{ data: [], page: { nextCursor } }`).
  //
  // NOT `apiList`. That helper maps `response.value.data` into `rows`, so here
  // `rows` is the OBJECT `{ people: [...] }`; reaching for `.people` on the
  // unwrapped value reads one level too high and yields undefined. A `?? []`
  // then turned that shape error into a SILENT empty directory, and every name
  // on every screen rendered "Unknown person" with the suite green — a failure
  // indistinguishable from missing data. There is deliberately no default: a
  // shape change must fail here rather than render as "nobody resolved".
  const response = await client.api<
    ApiEnvelope<{ readonly people: readonly RawPersonRow[] }>
  >('/v1/people');
  if (!response.ok) return response;
  return ok(response.value.data.people.map((row) => ({ user_id: row.userId, email: row.email })));
}

export async function listInvites(
  client: Client,
  orgId: string,
): Promise<Result<readonly InviteRow[]>> {
  void orgId;
  // `pending=true` becomes `accepted_at is null` in the query, before the limit.
  // Filtering after the page was taken spent a page of invites and threw the
  // accepted ones away, so an organization with a long invite history got a short
  // page and a cursor that looked like the end of the data.
  const page = await apiList<InviteRow>(client, '/v1/invites', { pending: 'true' });
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.created_at, 'desc'));
}

export async function listGroups(
  client: Client,
  orgId: string,
): Promise<Result<readonly GroupRow[]>> {
  void orgId;
  const page = await apiList<GroupRow>(client, '/v1/groups');
  if (!page.ok) return page;
  return ok(byField(page.value.rows, (row) => row.name));
}

export async function listAuditEvents(
  client: Client,
  orgId: string,
  limit: number,
): Promise<Result<readonly AuditEventRow[]>> {
  void orgId;
  // Paged, not clamped: the admin screen asks for up to 500 events and
  // `PAGE_SIZE_MAX` is 100, so one call cannot satisfy it. Silently returning the
  // first 100 would under-report the audit trail, which is the one listing where
  // a short answer reads as "nothing happened".
  const rows = await collect<AuditEventRow>(client, '/v1/audit-events', Math.max(1, limit));
  if (!rows.ok) return rows;
  return ok(byField(rows.value, (row) => row.occurred_at, 'desc'));
}

export async function setMembershipRole(
  client: Client,
  orgId: string,
  userId: string,
  role: 'admin' | 'member',
  idempotencyKey: string,
): Promise<Result<{ readonly userId: string; readonly role: string }>> {
  const response = await client.api<
    ApiEnvelope<{ readonly user_id: string; readonly role: string }>
  >(`/v1/orgs/${orgId}/members/${userId}`, {
    method: 'PUT',
    headers: { 'idempotency-key': idempotencyKey },
    body: { role },
  });
  if (!response.ok) return response;
  return ok({ userId: response.value.data.user_id, role: response.value.data.role });
}

/**
 * The audited self-review exception (D17, PUB-02).
 *
 * This is the ONLY way the flag changes from the product. Before
 * `0061_self_review_policy_rpc.sql` the column was reachable only by a raw
 * `UPDATE` on `app.organizations`, which any org-admin browser session could
 * do and which wrote no audit row — so the one control that disables the
 * reviewer/author separation had no trail. The revoke in that migration also
 * removed the ability to edit the organization row at all.
 *
 * `reason` is required by the RPC rather than optional here, because the change
 * is audited and the audit has to be able to say why somebody turned
 * segregation of duties off.
 */
export async function setSelfReviewPolicy(
  client: Client,
  orgId: string,
  allow: boolean,
  reason: string,
  idempotencyKey: string,
): Promise<Result<{ readonly allowSelfReview: boolean; readonly version: number }>> {
  const response = await client.api<
    ApiEnvelope<{ readonly allow_self_review: boolean; readonly version: number }>
  >(`/v1/orgs/${orgId}/policy/self-review`, {
    method: 'PUT',
    headers: { 'idempotency-key': idempotencyKey },
    body: { allow, reason },
  });
  if (!response.ok) return response;
  return ok({
    allowSelfReview: response.value.data.allow_self_review,
    version: response.value.data.version,
  });
}

export async function removeMembership(
  client: Client,
  orgId: string,
  userId: string,
  idempotencyKey: string,
): Promise<Result<{ readonly userId: string }>> {
  const response = await client.api<ApiEnvelope<{ readonly user_id: string }>>(
    `/v1/orgs/${orgId}/members/${userId}`,
    { method: 'DELETE', headers: { 'idempotency-key': idempotencyKey } },
  );
  if (!response.ok) return response;
  return ok({ userId: response.value.data.user_id });
}

/**
 * Create an invitation.
 *
 * NOT AVAILABLE IN THIS BUILD. The intended flow is described because it is what
 * the route must do once `app.create_invite` exists: the plaintext token exists
 * only in the response the administrator sees once, `accept_invite` compares the
 * hexadecimal SHA-256 of that token against `token_hash`, so what is stored is a
 * digest and the link is not recoverable afterwards. Seven days is V1 section 4's
 * stated default expiry.
 *
 * The digest belongs on the server, in the route that mints the token. A browser
 * that chooses its own hash is choosing the credential the server will accept.
 */

/**
 * Seven days, the default expiry `docs/V1.md` states for an invitation.
 * `app.create_invite` refuses a past `p_expires_at`, so this is a real bound and
 * not a formality: a caller that computed it wrongly gets 422 `invalid_expires_at`.
 */
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
export async function createInvite(
  client: Client,
  input: {
    readonly email: string;
    readonly role: 'admin' | 'member';
    readonly token: string;
  },
  idempotencyKey: string,
): Promise<Result<{ readonly invite: InviteRow; readonly link: string }>> {
  // `app.create_invite` takes `p_token_hash`, not the token. The plaintext exists
  // only in the `link` returned here, once: `accept_invite` compares the SHA-256
  // hex of the token it is given against that stored digest, so what lands in the
  // database cannot be reversed into a usable link afterwards.
  //
  // `org_id` is not a parameter — the route fills it from the verified token and
  // 422s a body carrying it, for the same reason `createSkill` does not take one.
  //
  // The hash is computed here rather than in the route because the plaintext
  // never leaves the browser: a route that minted the token would have to return
  // it anyway, and this keeps exactly one place that ever holds it.
  const digest = await sha256Hex(input.token);
  const response = await client.api<ApiEnvelope<InviteRow>>('/v1/invites', {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
    body: {
      email: input.email,
      role: input.role,
      token_hash: digest,
      expires_at: new Date(Date.now() + SEVEN_DAYS_MS).toISOString(),
    },
  });
  if (!response.ok) return response;
  // Through `globalThis`, not the bare `location` global. A bare reference is a
  // hard `ReferenceError` wherever there is no window — a test runner, a build
  // step — and this module is loaded in both. `client.ts` reads its injected
  // config the same way for the same reason.
  //
  // With no origin the link is root-relative, which is still a usable link in a
  // browser and an obviously-relative one anywhere else. Throwing here would
  // destroy an invitation that was created successfully and audited, which is a
  // far worse outcome than a link the caller has to qualify.
  const origin = globalThis.location?.origin ?? '';
  return ok({ invite: response.value.data, link: `${origin}/invite/${input.token}` });
}

export async function acceptInvite(
  client: Client,
  token: string,
  idempotencyKey: string,
): Promise<Result<{ readonly userId: string; readonly orgId: string }>> {
  const response = await client.api<
    ApiEnvelope<{ readonly user_id: string; readonly org_id: string }>
  >('/v1/invites/accept', {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
    body: { token },
  });
  if (!response.ok) return response;
  return ok({ userId: response.value.data.user_id, orgId: response.value.data.org_id });
}

/**
 * The published bundle, as the API serves it.
 *
 * This is the only read in the application that records an observed delivery:
 * `bundle.ts` writes the retrieval event before returning the bytes, so
 * downloading from here is what makes a retrieval appear in the audit trail.
 */
export async function downloadBundle(client: Client, skillId: string): Promise<Result<string>> {
  const response = await client.api<string>(`/v1/skills/${skillId}/bundle`, { expect: 'text' });
  if (!response.ok) return response;
  return ok(response.value);
}

/**
 * The native standard-folder export, exactly as the bytes arrived.
 *
 * `expect: 'bytes'`, and not `text` as `downloadBundle` uses, because this body
 * is a zip archive holding `SKILL.md`, the release's `references/`, `assets/`
 * and `scripts/`, and `release-manifest.json` beside the folder
 * (`packages/folder/src/layout.ts`). A decode here would produce an archive
 * that still opens and is no longer the release, which is a failure no digest
 * check on the server can catch because the corruption happens after it.
 *
 * It records a delivery, exactly as the download above does: `export.ts` writes
 * the retrieval event before returning, so an export that is not in
 * `app.retrieval_events` is a copy the audit trail cannot see.
 */
export async function downloadExport(
  client: Client,
  skillId: string,
): Promise<Result<Uint8Array<ArrayBuffer>>> {
  return client.api<Uint8Array<ArrayBuffer>>(`/v1/skills/${skillId}/export`, { expect: 'bytes' });
}

/**
 * Several skill rows by id.
 *
 * Used wherever a screen shows a title beside a draft, a proposal or a toolkit
 * item. The policies filter the result, so a title that comes back missing means
 * the caller may not see that skill rather than that it does not exist.
 */
export async function loadSkills(
  client: Client,
  ids: readonly string[],
): Promise<Result<readonly SkillRow[]>> {
  // One route call per id: `GET /v1/skills/:skillId` is the only skill read in
  // the contract and there is no bulk `?ids=`. A skill the policies hide 404s and
  // is dropped, because this caller's question is "which of these can I see", not
  // "does this id exist" — a hidden skill is an absent title, not an error.
  return loadById<SkillRow>(client, (id) => `/v1/skills/${encodeURIComponent(id)}`, ids);
}
/**
 * Observed deliveries for one client, newest first.
 *
 * The three facts this call can report are kept apart, and the reason it is
 * written this way is the bug it exists to fix: an empty array is
 * indistinguishable from "this client has never been used", and
 * `retrieval_events_read` does not hand every row in the organization to every
 * reader — an admin reads the whole organization, and since 0083 a member reads
 * their OWN deliveries and nothing of a colleague's (V1 §10:407). So an empty
 * array is ALSO what a filtered read returns for rows this caller is not
 * entitled to. One value was standing for three different facts — never used,
 * not permitted to see, and not implemented. This returns a `Result`, so a
 * failure is a distinct value the caller must handle rather than an empty array
 * it can silently render as "never".
 *
 * `orgId` is threaded but deliberately NOT sent. Every listing route is bound to
 * the organization in the verified token, so an org id here cannot narrow the
 * result; sending one would suggest the read is organization-agnostic when it
 * is not, and is the shape a cross-tenant leak takes. `queries.ts:354` states
 * the same rule for the other listings and it applies here unchanged.
 *
 * Ordered by `retrieved_at`, not by the order the route returned. The route's
 * keyset is `created_at desc, id desc` and says nothing about what this screen
 * wants, and the two are separate columns on an append-only table.
 */
export async function listClientContacts(
  client: Client,
  orgId: string,
  clientId: string,
): Promise<Result<readonly RetrievalEventRow[]>> {
  void orgId;
  // Bounded, not exhaustive: this screen asks "has this client ever reached
  // the server, and when", which the newest observation answers. `collect`
  // pages until it has the number asked for and returns early on a short page,
  // so a client with a long history costs one request, not one per delivery.
  const rows = await collect<RetrievalEventRow>(client, '/v1/retrieval-events', 25, {
    client_id: clientId,
  });
  if (!rows.ok) return rows;
  return ok(byField(rows.value, (row) => row.retrieved_at, 'desc'));
}

/**
 * Transfer ownership of a skill, with the reason the audit row will carry.
 *
 * V1 section 6 makes ownership transfer an audited act, and the route that
 * calls `app.transfer_skill_ownership` is the only path that records it. A
 * direct write to `app.skills.owner_id` would move the owner with no trail,
 * which is precisely the unrecoverable-ownership case the audit row exists to
 * prevent.
 */
export async function transferSkillOwnership(
  client: Client,
  skillId: string,
  newOwnerId: string,
  reason: string,
  idempotencyKey: string,
): Promise<Result<{ readonly skillId: string; readonly ownerId: string }>> {
  // `app.transfer_skill_ownership` is declared with return type `app.skills`
  // (0030_constant_work_visibility_check.sql:704), so the row carries the
  // skill's own columns (`id` and `owner_id`) and not a `skill_id` key. The
  // previous shape read `data.skill_id`, which is absent, so a successful
  // transfer was reported as "Ownership of undefined transferred to undefined".
  const response = await client.api<
    ApiEnvelope<{ readonly id: string; readonly owner_id: string }>
  >(`/v1/skills/${skillId}/owner`, {
    method: 'PUT',
    headers: { 'idempotency-key': idempotencyKey },
    body: { newOwnerId, reason },
  });
  if (!response.ok) return response;
  return ok({ skillId: response.value.data.id, ownerId: response.value.data.owner_id });
}

/**
 * Move a skill along `active -> deprecated -> retired`.
 *
 * TWO CALLS, NOT ONE WITH A `lifecycle` FIELD, because `apps/api` names the
 * transition in the path. A single "set state" endpoint would have to decide
 * which end of the chain the request meant, which is a second state machine
 * written in TypeScript that could disagree with the one in SQL — the argument
 * `apps/api/src/routes/decisions.ts` makes, and the same one here.
 *
 * `replacementSkillId` is optional and `null` means "name none", which the
 * database treats as KEEPING whatever replacement is already recorded rather
 * than clearing it (`0033_lifecycle_rpcs.sql:1658-1663`). There is no way to
 * clear it in this schema, so the screen never offers one.
 *
 * `reason` is required by both the route and the RPC, and this signature has no
 * optional form of it: a caller that has nothing to say has not decided to make
 * this change, and the type is where that is cheapest to say.
 */
export async function setSkillLifecycle(
  client: Client,
  input: {
    readonly skillId: string;
    readonly to: 'deprecated' | 'retired';
    readonly reason: string;
    readonly replacementSkillId?: string | null;
  },
  idempotencyKey: string,
): Promise<Result<SkillRow>> {
  const response = await client.api<ApiEnvelope<SkillRow>>(
    `/v1/skills/${encodeURIComponent(input.skillId)}/${input.to === 'retired' ? 'retire' : 'deprecate'}`,
    {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: {
        reason: input.reason,
        replacement_skill_id: input.replacementSkillId ?? null,
      },
    },
  );
  if (!response.ok) return response;
  return ok(response.value.data);
}
